import { useState, useEffect, useMemo } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Plus, Minus, FileText, AlertTriangle, Utensils } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import ScheduleDeliveryPicker from "./ScheduleDeliveryPicker";
import { formatPrice, resolveUrl, trackCartViewed } from "../webOrderApi";

// Adapted from web_order/src/components/CheckoutPage.tsx. Per the seller's
// explicit instructions this port drops:
//   - the address selector/header line and the whole delivery-details card
//     (delivery is handled on WhatsApp afterward, not on the web page)
//   - the "Delivery Fee: FREE ₹56.64" bill line and the "You saved ₹56.64"
//     banner (both were hardcoded/fake in the original, unrelated to any
//     real discount, and don't apply now that delivery isn't priced here)
// The seller-note field is wired up for real this time (it was present but
// never actually read anywhere in the original prototype).
//
// Place Order calls the real checkout API (via onPlaceOrder), which
// validates the cart and returns a pre-filled order-summary text (the cart
// itself isn't committed yet — see ChatflowEngineService.prepareWebCheckout).
// A validation failure (e.g. an item went out of stock) shows an inline
// error + Try Again, without ever redirecting. On success, the browser goes
// to wa.me with that summary PRE-FILLED in the compose box — WhatsApp
// itself doesn't allow auto-sending, so the customer still has to tap Send
// there themselves; only that confirms the order (see the WEB_CHECKOUT
// dispatch branch in chatflow-engine.service.ts, which only proceeds if the
// message they actually send contains this summary's "Order Ref" token).
//
// No "Order Placed Successfully!" page — tapping Place Order and it
// succeeding is the last thing that happens on the web UI itself.
export default function CheckoutPage({
  isOpen,
  onClose,
  sessionId,
  shopName,
  logo,
  cartItems,
  products = [],
  onUpdateQuantity,
  onUpdateGroupQuantity,
  onEditCartItem,
  onEditGroup,
  onPlaceOrder,
  onRemoveInvalidItems,
  waNumber,
  gstPercent,
  gstInclusive,
  freeDeliveryWithinKm,
  deliveryPaused = false,
  deliveryHoursClosed = false,
  deliveryHoursClosedMessage,
  deliveryTodayLabel,
  scheduledDelivery = null,
}) {
  const [note, setNote] = useState("");
  const [isBillSummaryOpen, setIsBillSummaryOpen] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState(null);
  // Instant Order / Prebooking (Dropit sellers only) — Prebooking slots
  // come from the storefront payload, or fresh from a checkout reply when
  // the page's own list was missing or out of date.
  const [freshScheduleDays, setFreshScheduleDays] = useState(null);
  const [orderMode, setOrderMode] = useState("instant");
  const [selectedScheduleDay, setSelectedScheduleDay] = useState(null);
  const [selectedScheduleSlot, setSelectedScheduleSlot] = useState(null);
  const scheduleDays = freshScheduleDays || scheduledDelivery?.days || null;
  const prebookingAvailable = !!scheduleDays?.some((d) => d.slots?.length > 0);
  const isPrebooking = prebookingAvailable && orderMode === "prebook";

  const [closedNoticeVisible, setClosedNoticeVisible] = useState(false);
  const closedNoticeText =
    deliveryTodayLabel && deliveryTodayLabel !== "Closed"
      ? `⏰ We're not delivering right now. Our delivery hours today are ${deliveryTodayLabel}. You can choose Prebooking to schedule your delivery.`
      : "⏰ We're not delivering today. You can choose Prebooking to schedule your delivery.";

  // Outside delivery hours the notice shows at the top and Prebooking is
  // selected for the customer straight away.
  const showClosedNotice = () => {
    setOrderMode("prebook");
    setClosedNoticeVisible(true);
  };

  useEffect(() => {
    if (isOpen && prebookingAvailable && deliveryHoursClosed) {
      setOrderMode("prebook");
      setClosedNoticeVisible(true);
    }
  }, [isOpen, prebookingAvailable, deliveryHoursClosed]);
  const [brokenImages, setBrokenImages] = useState(() => new Set());

  // Fires every single time this screen opens (this component stays
  // mounted and just returns null while closed — see the `if (!isOpen)
  // return null` below — so `isOpen` flipping true is a real re-open, not a
  // mount). Deliberately NOT deduped the way trackPageView is — see
  // trackCartViewed's own doc comment.
  useEffect(() => {
    if (isOpen) trackCartViewed(sessionId);
  }, [isOpen, sessionId]);

  // Independent-mode products (e.g. "pick one Cold Salad AND one Hot Salad")
  // are really several cart lines under the hood — one per selected group
  // (see ProductModal.jsx) — but they're all ONE purchase of ONE main
  // product, so they're shown here as a single grouped card with every
  // selected option listed underneath, instead of one row per line.
  // Combination-mode products (Default unchecked) are untouched — each of
  // those variants is a genuinely separate SKU, so they keep their own row.
  const rows = useMemo(() => {
    const result = [];
    const seenGroupProductIds = new Set();
    cartItems.forEach((item) => {
      const product = products.find((p) => p.id === item.productId);
      if (product?.independentVariants) {
        if (seenGroupProductIds.has(item.productId)) return;
        seenGroupProductIds.add(item.productId);
        const groupItems = cartItems.filter((i) => i.productId === item.productId);
        result.push({
          type: "group",
          productId: item.productId,
          product,
          items: groupItems,
          quantity: groupItems[0]?.quantity ?? 1,
          unitTotal: groupItems.reduce((sum, i) => sum + i.unitPrice, 0),
          originalUnitTotal: groupItems.reduce((sum, i) => sum + i.originalPrice, 0),
          image: groupItems[0]?.image || null,
        });
      } else {
        result.push({ type: "single", item });
      }
    });
    return result;
  }, [cartItems, products]);

  // window.location.href = "https://wa.me/..." below often just triggers the
  // OS to switch to the WhatsApp app (an intent-style handoff) without this
  // page actually unloading — especially in mobile in-app/webview browsers.
  // If that happens, `placing` stays stuck true forever with nothing to
  // reset it. So: while we're mid-redirect, watch for this tab becoming
  // visible/focused again (i.e. the customer came back instead of sending
  // the WhatsApp message) and treat that as "they changed their mind" —
  // release the button so they can edit the cart and try again.
  useEffect(() => {
    if (!placing) return;
    const resetIfStillPlacing = () => {
      if (document.visibilityState === "visible") setPlacing(false);
    };
    document.addEventListener("visibilitychange", resetIfStillPlacing);
    window.addEventListener("focus", resetIfStillPlacing);
    return () => {
      document.removeEventListener("visibilitychange", resetIfStillPlacing);
      window.removeEventListener("focus", resetIfStillPlacing);
    };
  }, [placing]);

  if (!isOpen) return null;

  const itemTotal = cartItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  // Combined CGST+SGST on the item total — same rule the WhatsApp order
  // summary uses (buildWebOrderPrefillText), so what's shown here always
  // matches what the customer sees again on WhatsApp. 0/unset seller GST
  // means no GST line at all, not a ₹0.00 row.
  const gstAmount = Number(gstPercent) > 0 ? itemTotal * (Number(gstPercent) / 100) : 0;
  // Exclusive (default): GST is added on top of the item total. Inclusive:
  // the seller's prices already include it — still shown as its own line
  // above for reference, but never added a second time here.
  const toPay = gstInclusive ? itemTotal : itemTotal + gstAmount;

  const handlePlaceOrder = async () => {
    setError(null);
    // Settings → Order Settings → "Temporarily Disable Deliveries" — checked
    // client-side first so the customer sees the notice instantly instead of
    // waiting on a network round-trip; the server independently rejects the
    // same way too (see chatflow-engine.service.ts), so a stale cached
    // storefront.deliveryPaused value can never let an order through.
    if (deliveryPaused) {
      setError(
        <>
          🚚 Delivery is currently unavailable at this time.
          <br />
          <br />
          To arrange delivery, please contact us at this number:
          <br />
          📞{" "}
          <a href="tel:+916353733533" className="underline">
            +91 6353733533
          </a>
        </>
      );
      return;
    }
    // Settings → Business Profile → Delivery Hours → "Block Orders Outside
    // Delivery Hours" — deliberately NOT checked client-side first (unlike
    // deliveryPaused above). This always has to reach the server: that's the
    // one place that registers the customer for the automatic "delivery is
    // available again" WhatsApp message once hours reopen (see
    // ChatflowEngineService.prepareWebCheckout). A client-side short-circuit
    // here — using the page's own cached deliveryHoursClosed snapshot from
    // when it loaded — used to skip calling the server entirely whenever
    // that snapshot already said "closed" (i.e. any time someone opens the
    // link after hours, not just the rare case of hours closing mid-visit),
    // which silently meant that registration never happened. The server
    // re-checks the exact same thing and returns the identical configured
    // message, so removing this costs nothing except one network round trip
    // on the (already unhappy) closed-hours path.
    //
    // Exception: when Prebooking is offered (Dropit sellers) an Instant Order
    // outside delivery hours is stopped here with the Prebooking warning —
    // Dropit sellers never get that "available again" message anyway.
    if (isPrebooking && !selectedScheduleSlot) {
      setError("Please select a delivery date and time.");
      return;
    }
    if (!isPrebooking && prebookingAvailable && deliveryHoursClosed) {
      showClosedNotice();
      return;
    }
    setPlacing(true);
    try {
      const result = await onPlaceOrder(note, isPrebooking ? selectedScheduleSlot : null);
      if (!result?.success) {
        if (result?.scheduledDelivery?.days) {
          setFreshScheduleDays(result.scheduledDelivery.days);
          setSelectedScheduleSlot(null);
        }
        if (!isPrebooking && result?.scheduledDelivery?.days && !result?.invalidItems?.length) {
          showClosedNotice();
        } else if (result?.invalidItems?.length > 0) {
          // Remove exactly the stale line(s) so the rest of the cart stays
          // intact — the customer can review what's left and place the
          // order again immediately instead of being stuck on a cart that
          // will never succeed.
          onRemoveInvalidItems?.(result.invalidItems);
          const reasons = result.invalidItems.map((i) => i.reason).filter(Boolean);
          setError(
            `${reasons.join(". ")}. Removed from your cart — please review and place your order again.`
          );
        } else {
          setError(result?.message || "Something went wrong. Please try again.");
        }
        setPlacing(false);
        return;
      }
      const prefill = result.prefillText ? `?text=${encodeURIComponent(result.prefillText)}` : "";
      window.location.href = waNumber ? `https://wa.me/${waNumber}${prefill}` : "/";
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong. Please try again.");
      setPlacing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#F3F4F6] overflow-y-auto flex flex-col font-sans animate-in fade-in duration-200">
      {/* Same header /order itself uses — back button, store logo, static
          veg indicator — for visual continuity between the two, instead of
          this page's own previously-distinct plain header. */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-3 py-2.5 shadow-2xs w-full">
        <div className="w-full flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={placing}
            aria-label="Back"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#520400] text-white shadow-sm transition-transform active:scale-95 disabled:opacity-40"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          {logo && (
            <img src={resolveUrl(logo)} alt="" className="h-10 w-auto shrink-0 object-contain" />
          )}
          <div className="ml-auto flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-50 px-2 py-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border-2 border-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
            </span>
            <span className="text-[10px] font-extrabold uppercase tracking-wide text-emerald-700">Veg</span>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full p-2 sm:p-4 space-y-4 pb-24">
        {error && (
              <div className="w-full bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-700">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="mt-1 text-xs font-bold text-red-600 hover:underline cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Purely informational — the storefront doesn't price delivery
                pre-chat (see file header note above), so this is just a
                heads-up, not a bill line. null = no "Free delivery within X
                km" rule configured at all. 0 = always free, no distance cap. */}
            {freeDeliveryWithinKm != null && (
              <div className="w-full bg-[#DCFCE7] border border-[#86EFAC] rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <span className="text-base leading-none">🚴</span>
                <span className="text-xs sm:text-sm font-bold text-[#15803D]">
                  {freeDeliveryWithinKm === 0 ? "Free Delivery" : `Free Delivery within ${freeDeliveryWithinKm} KM`}
                </span>
              </div>
            )}

            <div className="w-full bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden">
              <div className="divide-y divide-dashed divide-slate-200/90">
                {rows.map((row) =>
                  row.type === "group" ? (
                    <div key={`group-${row.productId}`} className="p-4 flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => onEditGroup?.(row.productId)}
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200/80 shrink-0 cursor-pointer"
                      >
                        {row.image && !brokenImages.has(`group-${row.productId}`) ? (
                          <img
                            src={row.image}
                            alt={row.product.name}
                            onError={() => setBrokenImages((prev) => new Set(prev).add(`group-${row.productId}`))}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Utensils className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
                          </div>
                        )}
                      </button>

                      <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
                        <button
                          type="button"
                          onClick={() => onEditGroup?.(row.productId)}
                          className="flex-1 min-w-0 text-left cursor-pointer"
                        >
                          <h3 className="text-sm sm:text-base font-extrabold text-slate-900 truncate mb-1 hover:underline">{row.product.name}</h3>
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-xs font-semibold text-slate-600">{formatPrice(row.unitTotal)}</span>
                            {row.originalUnitTotal > row.unitTotal && (
                              <span className="text-[11px] font-semibold text-slate-400 line-through">{formatPrice(row.originalUnitTotal)}</span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            <span className="font-semibold text-slate-600">Selected options:</span>
                            <ul className="mt-0.5 space-y-0.5 list-disc list-inside">
                              {row.items.map((i) => (
                                <li key={i.cartKey} className="font-medium">{i.variantLabel}</li>
                              ))}
                            </ul>
                          </div>
                        </button>

                        <div className="flex flex-col items-end shrink-0">
                          <div className="bg-[#520400] rounded-xl px-2 py-1 font-extrabold text-xs sm:text-sm flex items-center gap-2 shadow-2xs">
                            <button
                              onClick={() => onUpdateGroupQuantity?.(row.productId, row.quantity - 1)}
                              className="w-5 h-5 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md cursor-pointer transition-colors"
                            >
                              <Minus className="w-3.5 h-3.5 stroke-[3]" />
                            </button>
                            <span className="min-w-[14px] text-center font-black text-white">{row.quantity}</span>
                            <button
                              onClick={() => onUpdateGroupQuantity?.(row.productId, row.quantity + 1)}
                              className="w-5 h-5 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md cursor-pointer transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5 stroke-[3]" />
                            </button>
                          </div>
                          <div className="text-sm font-black text-slate-900 mt-2">{formatPrice(row.unitTotal * row.quantity)}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={row.item.cartKey} className="p-4 flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => onEditCartItem?.(row.item)}
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200/80 shrink-0 cursor-pointer"
                      >
                        {row.item.image && !brokenImages.has(row.item.cartKey) ? (
                          <img
                            src={row.item.image}
                            alt={row.item.name}
                            onError={() => setBrokenImages((prev) => new Set(prev).add(row.item.cartKey))}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Utensils className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
                          </div>
                        )}
                      </button>

                      <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
                        <button
                          type="button"
                          onClick={() => onEditCartItem?.(row.item)}
                          className="flex-1 min-w-0 text-left cursor-pointer"
                        >
                          <h3 className="text-sm sm:text-base font-extrabold text-slate-900 truncate mb-1 hover:underline">{row.item.name}</h3>
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-xs font-semibold text-slate-600">{formatPrice(row.item.unitPrice)}</span>
                            {row.item.originalPrice > row.item.unitPrice && (
                              <span className="text-[11px] font-semibold text-slate-400 line-through">{formatPrice(row.item.originalPrice)}</span>
                            )}
                          </div>
                          {row.item.variantLabel && <div className="text-xs font-medium text-slate-500 mt-0.5">{row.item.variantLabel}</div>}
                        </button>

                        <div className="flex flex-col items-end shrink-0">
                          <div className="bg-[#520400] rounded-xl px-2 py-1 font-extrabold text-xs sm:text-sm flex items-center gap-2 shadow-2xs">
                            <button
                              onClick={() => onUpdateQuantity(row.item.cartKey, row.item.quantity - 1)}
                              className="w-5 h-5 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md cursor-pointer transition-colors"
                            >
                              <Minus className="w-3.5 h-3.5 stroke-[3]" />
                            </button>
                            <span className="min-w-[14px] text-center font-black text-white">{row.item.quantity}</span>
                            <button
                              onClick={() => onUpdateQuantity(row.item.cartKey, row.item.quantity + 1)}
                              className="w-5 h-5 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md cursor-pointer transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5 stroke-[3]" />
                            </button>
                          </div>
                          <div className="text-sm font-black text-slate-900 mt-2">{formatPrice(row.item.unitPrice * row.item.quantity)}</div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="px-4 py-3 border-t border-dashed border-slate-200/90">
                <button onClick={onClose} className="text-xs sm:text-sm font-extrabold text-[#520400] hover:underline flex items-center gap-1 cursor-pointer">
                  <span className="text-base font-bold">+</span>
                  <span>Add more items</span>
                </button>
              </div>

              <div className="p-3.5 border-t border-slate-100 bg-slate-50/60">
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <FileText className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Write a note / instructions..."
                    className="w-full pl-9 pr-3 py-2.5 text-xs font-medium border border-slate-200 rounded-xl focus:outline-none focus:border-slate-400 bg-white text-slate-900 placeholder-slate-400"
                  />
                </div>
              </div>
            </div>

            <div className="w-full bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-2xs">
              <button
                type="button"
                onClick={() => setIsBillSummaryOpen(!isBillSummaryOpen)}
                className={`w-full px-4 py-3 flex items-center justify-between text-xs font-black text-slate-800 uppercase tracking-wide hover:bg-slate-50 cursor-pointer transition-colors text-left ${
                  isBillSummaryOpen ? "border-b border-dashed border-slate-200" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>🧾</span>
                  <span>BILL SUMMARY</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isBillSummaryOpen ? "rotate-0" : "-rotate-180"}`} />
              </button>

              {isBillSummaryOpen && (
                <div className="p-4 space-y-3 text-xs sm:text-sm">
                  <div className="flex items-center justify-between font-medium text-slate-700">
                    <div className="flex items-center gap-2">
                      <span>📋</span>
                      <span>Item total</span>
                    </div>
                    <span className="font-bold text-slate-900">{formatPrice(itemTotal)}</span>
                  </div>

                  {gstAmount > 0 && (
                    <div className="flex items-center justify-between font-medium text-slate-700">
                      <div className="flex items-center gap-2">
                        <span>🧾</span>
                        <span>{gstInclusive ? "GST (Including)" : "GST"}</span>
                      </div>
                      <span className="font-bold text-slate-900">{formatPrice(gstAmount)}</span>
                    </div>
                  )}

                  {/* Delivery is priced on WhatsApp once the customer shares
                      their location, not here — freeDeliveryWithinKm === 0
                      is the one case where it's unconditionally free (see
                      the banner above), so this note is skipped only then. */}
                  {freeDeliveryWithinKm !== 0 && (
                    <div className="flex items-center justify-between gap-3 font-medium text-slate-700">
                      <div className="flex items-center gap-2 shrink-0">
                        <span>🚴</span>
                        <span>Delivery Fee</span>
                      </div>
                      <span className="font-bold text-red-600 text-right">Calculated after sharing location on WhatsApp</span>
                    </div>
                  )}

                  <div className="border-t border-dashed border-slate-200 pt-3 flex items-center justify-between text-sm sm:text-base font-black text-slate-900">
                    <div className="flex items-center gap-2">
                      <span>💳</span>
                      <span>To pay</span>
                    </div>
                    <span>{formatPrice(toPay)}</span>
                  </div>
                </div>
              )}
            </div>
            {prebookingAvailable && (
              <ScheduleDeliveryPicker
                days={scheduleDays}
                mode={orderMode}
                onModeChange={(m) => {
                  setOrderMode(m);
                  setError(null);
                }}
                deliveryClosed={deliveryHoursClosed}
                notice={closedNoticeVisible ? closedNoticeText : null}
                selectedDay={selectedScheduleDay}
                selectedSlot={selectedScheduleSlot}
                onSelectDay={(key) => {
                  setSelectedScheduleDay(key);
                  setSelectedScheduleSlot(null);
                }}
                onSelectSlot={(value) => {
                  setSelectedScheduleSlot(value);
                  setError(null);
                }}
              />
            )}
      </main>

      <div className="sticky bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200/80 p-3 sm:p-4 shadow-xl w-full">
        <button
          onClick={handlePlaceOrder}
          disabled={placing || cartItems.length === 0}
          className="w-full bg-[#54AD56] hover:bg-[#46A248] active:scale-98 disabled:opacity-60 disabled:cursor-not-allowed text-white font-extrabold text-sm sm:text-base py-3.5 px-6 rounded-xl sm:rounded-2xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-1 text-center"
        >
          <span className="truncate">
            {placing ? "Saving Order..." : <>{formatPrice(toPay)} • Place Order on WhatsApp</>}
          </span>
          {!placing && <FaWhatsapp className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />}
          {!placing && <ChevronRight className="w-4 h-4 stroke-[3] inline-block ml-0.5 shrink-0" />}
        </button>
      </div>
    </div>
  );
}
